// hooks/useTicketDetails.ts
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "../api";

export interface TicketDetails {
  pikitup_ticket_id: number;
  service_type: number;
  status_flg: number;
  status_text: string;
  assignee_name: string;
  pikitup_cso_name: string;
  service_short_description: string;
  service_long_description: string;
  service_ticket_no: string;
  service_address_info: string;
  customer_first_name: string;
  customer_surname: string;
  created_date: string;
  files: {
    pikitup_ticket_image_id: number;
    filepath: string;
    file_mimetype: string;
  }[];
  activity_logs: {
    created_at: string;
    comment: string | null;
    prev_status: string;
    new_status: string;
    files: any[];
  }[];
  related_tickets_count: string;
  related_tickets: any[];
}

export interface AccountDetails {
  csd_contract_id: number;
  account_no: string;
  property_type: string;
  property_owner: string;
  email: string;
  contact_no: string;
  property_address: string;
}

export const useTicketDetails = (ticketId: string | undefined) => {
  return useQuery({
    queryKey: ["ticketDetails", ticketId],
    queryFn: async () => {
      // 1️⃣ Fetch ticket details
      if (!ticketId) throw new Error("Ticket ID is required");

      const ticketRes = await api.get(
        `/tickets/details/v1?ticket_id=${ticketId}`
      );

      if (ticketRes.data.statusCode !== 200)
        throw new Error(ticketRes.data.message || "Failed to fetch ticket");

      const ticketData: TicketDetailsData = ticketRes.data.data;

      let accountNo = ticketData?.account_no;
      let binType: string | undefined = undefined;

      if (ticketData.ticket_products && ticketData.ticket_products.length > 0) {
        const firstProduct = ticketData.ticket_products[0];

        // Extract account_no from bin_details if not already available
        if (!accountNo && firstProduct?.bin_details?.length > 0) {
          accountNo = firstProduct.bin_details[0]?.account_no;
        }

        // Extract bin_type from the first product
        binType = firstProduct?.bin_type;
      }

      console.log("binType", binType);

      let accountDetails: AccountDetails | null = null;
      let pickitupServiceList: any = null;
      let pikitupMeta: any = null;

      // Call account-details API if account number exists
      if (ticketData.service_type === 1 && accountNo) {
        const accRes = await api.get(
          `/account-details/v1?account_no=${accountNo}`
        );

        if (accRes.data.status === 200) {
          accountDetails = accRes.data.data;
        }
      }

      //  pikitup-service-lists API
      if (
        accountNo &&
        binType &&
        ticketData.ticket_products &&
        ticketData.ticket_products.length > 0
      ) {
        const serviceRes = await api.get(
          `/pikitup-service-lists/v1?account_no=${accountNo}&bin_type=${binType}&page=1&perPage=10`
        );

        if (serviceRes.data.statusCode === 200) {
          pickitupServiceList = serviceRes.data.data?.service_list || [];
          pikitupMeta = serviceRes.data.data?.meta || null;
        }
      } else {
        console.log("Skipping pikitup-service-lists API because:", {
          hasAccountNo: !!accountNo,
          hasBinType: !!binType,
          hasTicketProducts: ticketData.ticket_products?.length > 0,
        });
      }

      // Return both ticket + account details
      return {
        ticket: ticketData,
        account: accountDetails,
        pikitupServices: pickitupServiceList,
        pikitupMeta: pikitupMeta,
      };
    },
    enabled: !!ticketId,
  });
};

export const useScheduleDelivery = async (payload: {
  pikitup_ticket_id: number;
  scheduled_date: string;
  schedule_time_of_day: string;
}) => {
  const res = await api.post(`/tickets/schedule-delivery/v1`, payload);
  return res.data;
};

export const useTicketApproval = () => {
  return useMutation({
    mutationFn: async (payload: {
      pikitup_ticket_id: number;
      is_payment_required?: boolean;
      product_details: {
        pickup_ticket_product_detail_id: number;
        requested_count: number;
        approved_count: number;
      }[];
    }) => {
      const res = await api.post(`/tickets/approval/v1`, payload);
      return res.data;
    },
  });
};
